import { Checkbox, Grid, SvgIcon, Tooltip, Typography } from '@mui/material';
import AGGridComponent from 'components/AgGrid/AGGridComponent';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ApproveIcon from 'assets/icons/agreement-status/approved-status-icon.svg?react';
import ResubmitIcon from 'assets/icons/agreement-status/clarification-needed-status-icon.svg?react';
import RequestClarificationIcon from 'assets/icons/agreement-status/clarification-needed-status-icon.svg?react';
import InProgressIcon from 'components/AgGrid/assets/task-configuration-icons/in-progress-icon.svg?react';
import RejectedIcon from 'assets/icons/agreement-status/rejected-status-icon.svg?react';
import EyeIcon from 'assets/icons/eye.svg?react';
import Inactive from 'assets/icons/agreement-status/inactive-status-icon.svg?react';
import CommonDialog from 'components/common/CommonDialog';
import ProductApproval from './ProductApproval';
import CloseIcon from 'assets/icons/close-icon.svg?react';
import { ColDef, ColGroupDef, ICellRendererParams } from 'ag-grid-community';
import { dateTimeValueFormatter } from 'components/AgGrid/gridComponents/helpers';
import AddDocument from 'assets/icons/add-document-icon.svg?react';
import { Task } from 'pco-product-service-internal-consumer';
import CustomStepMetadata from 'components/customStepMetadata/CustomStepMetadata';
import { Box } from '@mui/system';
import { setOpenedTaskUid } from 'reducers/serviceAgreementsReducer';
import { useDispatch } from 'react-redux';

type Props = {
  tasks: Task[];
};

const TaskOverview = ({ tasks }: Props) => {
  const [isFormulateDecisionDialogOpen, setIsFormulateDecisionDialogOpen] = useState(false);
  const [task, setTask] = useState<Task | undefined>(undefined);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const dispatch = useDispatch();

  const customStepMetadata = {
    taskId: task?.taskUid,
    componentProps: task?.content,
    customComponent: task?.customComponentName,
    workAccess: false,
    isPmkTask: false,
  };

  const getIcon = (resolution: string) => {
    switch (resolution) {
      case 'Approve':
        return <ApproveIcon />;
      case 'Resubmit':
        return <ResubmitIcon />;
      case 'Request Clarification':
        return <RequestClarificationIcon />;
      case 'Rejected':
        return <RejectedIcon />;
      case 'In progress':
        return <InProgressIcon />;
      case 'Withdrawn':
        return <Inactive />;
      default:
        return <ApproveIcon />;
    }
  };

  const handleOpenTask = useCallback(
    (params: { data?: Task }) => {
      if (!params.data) return;
      const { taskUid } = params.data;
      if (params.data.resolution !== 'In progress') {
        setIsReadOnly(true);
      }

      setTask(params.data);
      setIsFormulateDecisionDialogOpen(true);

      dispatch(setOpenedTaskUid(taskUid));
    },
    [dispatch],
  );

  const handleCloseTask = () => {
    setIsFormulateDecisionDialogOpen(false);
    dispatch(setOpenedTaskUid(null));
  };

  useEffect(() => {
    if (!isFormulateDecisionDialogOpen) {
      dispatch(setOpenedTaskUid(null));
    }
  }, [isFormulateDecisionDialogOpen, dispatch]);

  const getColumnDefs = useMemo((): (ColDef | ColGroupDef)[] => {
    return [
      {
        headerName: 'Task ID',
        field: 'taskUid',
        hide: true,
      },
      {
        headerName: 'Task',
        field: 'taskName',
        minWidth: 90,
      },
      {
        headerName: 'Assignee',
        field: 'assignee',
        maxWidth: 140,
      },

      {
        headerName: 'Resolution',
        field: 'resolution',
        minWidth: 120,
        cellRenderer: (params: ICellRendererParams<Task>) => {
          return (
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Box
                style={{
                  display: 'grid',
                  paddingRight: '0.5em',
                }}
              >
                {getIcon(params.value as string)}
              </Box>
              <Box> {params.value as string}</Box>
            </Box>
          );
        },
      },
      {
        headerName: 'Updated',
        field: 'lastUpdate',
        minWidth: 120,
        cellRenderer: (params: ICellRendererParams<Task>) => {
          const date = dateTimeValueFormatter({ value: params.value as string });

          if (!date) return null;

          const cellValue = date.toString();
          const cellWidth = params.column?.getActualWidth() || 0;

          // Create a temporary span element to measure the text width
          const tempSpan = document.createElement('span');
          tempSpan.innerText = cellValue;
          tempSpan.style.visibility = 'hidden';
          document.body.appendChild(tempSpan);

          // Check if the text width is greater than the cell width
          const cellContentWidth = tempSpan.offsetWidth > cellWidth - 30;

          // Remove the temporary span element
          document.body.removeChild(tempSpan);

          if (cellContentWidth) {
            return (
              <Tooltip title={cellValue} arrow>
                <span>{cellValue}</span>
              </Tooltip>
            );
          } else {
            return <span>{cellValue}</span>;
          }
        },
      },
      {
        headerName: '',
        field: 'id',
        minWidth: 70,
        width: 70,
        filter: false,
        maxWidth: 70,
        cellRenderer: (params: ICellRendererParams<Task>) => {
          return params?.data?.resolution === 'In progress' || params?.data?.resolution === 'Withdrawn' ? null : (
            <Tooltip title="View Task" arrow placement="top">
              <SvgIcon
                onClick={() => handleOpenTask(params)}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  color: 'gray',
                  cursor: 'pointer',
                  mt: 1,
                }}
              >
                <EyeIcon />
              </SvgIcon>
            </Tooltip>
          );
        },
      },
    ];
  }, [handleOpenTask]);

  return (
    <Grid container data-testid="task-overview" item xs={12} sx={{ display: 'flex', height: '100%', width: '100%' }}>
      <AGGridComponent
        id={'taskOverview'}
        rowKey={'taskUid'}
        gridStyle={{ minHeight: '6em' }}
        getColumnDefs={getColumnDefs}
        items={(tasks ?? []) as unknown as Record<string, unknown>[]}
      />
      <CommonDialog
        title={task ? task.taskName : undefined}
        isOpen={isFormulateDecisionDialogOpen}
        onClose={handleCloseTask}
        titleIcon={<AddDocument />}
        closeIcon={<CloseIcon onClick={handleCloseTask} />}
        content={
          task?.customComponentName === 'UploadDocumentAndSelectAsssignee' ||
          task?.customComponentName === 'UploadDocumentAndDistribute' ? (
            <Grid container>
              {JSON.parse(task?.content ?? '')?.userName && (
                <Grid container sx={{ mt: 1 }}>
                  <Typography variant="h6">Assignee: {JSON.parse(task?.content ?? '')?.userName}</Typography>
                </Grid>
              )}
              <Grid container sx={{ mt: 1 }}>
                <Typography variant="h6">File name: {JSON.parse(task?.content ?? '')?.fileName}</Typography>
              </Grid>
              {JSON.parse(task?.content ?? '')?.languange && (
                <Grid container sx={{ mt: 1 }}>
                  <Typography variant="h6">Languange: {JSON.parse(task?.content ?? '')?.languange}</Typography>
                </Grid>
              )}
              {JSON.parse(task?.content ?? '')?.checkBoxText && (
                <Grid
                  container
                  sx={{
                    mt: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                  }}
                >
                  <Grid container>
                    <Checkbox checked />
                    <Typography variant="h6">{JSON.parse(task?.content ?? '')?.checkBoxText[0]}</Typography>
                  </Grid>
                  <Grid container>
                    <Checkbox checked />
                    <Typography variant="h6">{JSON.parse(task?.content ?? '')?.checkBoxText[1]}</Typography>
                  </Grid>
                </Grid>
              )}
              <Grid container sx={{ mt: 1 }}>
                <Typography variant="h6">Form code: {JSON.parse(task?.content ?? '')?.formCode}</Typography>
              </Grid>
            </Grid>
          ) : task?.customComponentName ? (
            <CustomStepMetadata
              isReadOnly={true}
              customStepMetadata={customStepMetadata}
              closeAction={handleCloseTask}
            />
          ) : (
            <ProductApproval
              isReadOnly={isReadOnly}
              attributes={task}
              handleCancel={handleCloseTask}
              hasHandleCancel={false}
            />
          )
        }
      />
    </Grid>
  );
};

export default TaskOverview;
